/**
 * F49 (CIA-F1, решение В-19 б) — фильтр задач в адресе страницы коротким ключом.
 *
 * Сам фильтр в строку запроса не помещается: правил до пятидесяти, а nginx
 * отбивает строку длиннее 8 КБ ещё до приложения. Поэтому фильтр кладётся на
 * сервер черновиком (`POST /task-module/filter-drafts`, B44), а в адрес уходит
 * ключ `?f=<key>`. Перезагрузка и ссылка восстанавливают фильтр по ключу.
 *
 * ЧЕГО НЕ ДЕЛАЕТ: не делает ссылку общей. Черновик читает только его автор в своей
 * компании (так решено в B44) — чужой ключ отвечает 404 так же, как истёкший, и
 * экран говорит «ссылка устарела». Срок ключа — 30 дней.
 */
import { TaskFilterShared } from '@staiter/shared/task-module';
import { z } from 'zod';
import { ApiRequestError, apiRequest } from '~/lib/api/client';

/** Ключ черновика: 10 символов base62 — та же форма, что проверяет сервер. */
export const FILTER_DRAFT_KEY_RE = /^[A-Za-z0-9]{10}$/;

type DraftBody = {
  match?: TaskFilterShared['match'];
  rules?: TaskFilterShared['rules'];
  params?: Record<string, string | boolean | number | string[]>;
};

const DraftCreated = z.object({ key: z.string(), expires_at: z.string() });
const DraftRead = z.object({
  key: z.string(),
  filter: z.object({
    match: z.unknown().optional(),
    rules: z.unknown().optional(),
    params: z.record(z.string(), z.unknown()).optional(),
  }),
  expires_at: z.string(),
});

/** Пустой фильтр в адрес не кладётся: ключ без условий — лишняя строка в таблице. */
export function isEmptyFilter(f: TaskFilterShared): boolean {
  return Object.keys(toDraftBody(f)).length === 0;
}

/** Фильтр экрана → тело черновика: правила отдельно, фасеты плоскими значениями. */
export function toDraftBody(f: TaskFilterShared): DraftBody {
  const { match, rules, ...rest } = f;
  const params: NonNullable<DraftBody['params']> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    params[k] = v as string | boolean | number | string[];
  }
  return {
    ...(rules?.length ? { rules, ...(match ? { match } : {}) } : {}),
    ...(Object.keys(params).length > 0 ? { params } : {}),
  };
}

/** Тело черновика → фильтр экрана; то, что схема не узнаёт, отбрасывается целиком. */
export function fromDraftBody(body: z.infer<typeof DraftRead>['filter']): TaskFilterShared | null {
  const parsed = TaskFilterShared.safeParse({
    ...(body.params ?? {}),
    ...(body.match !== undefined ? { match: body.match } : {}),
    ...(body.rules !== undefined ? { rules: body.rules } : {}),
  });
  return parsed.success ? parsed.data : null;
}

export async function createFilterDraft(f: TaskFilterShared): Promise<string> {
  const res = await apiRequest('/task-module/filter-drafts', DraftCreated, {
    method: 'POST',
    body: { filter: toDraftBody(f) },
  });
  return res.key;
}

/**
 * Фильтр по ключу. `null` — ключа нет для этого пользователя: истёк, чужой или не
 * существовал (сервер эти случаи не различает намеренно).
 */
export async function fetchFilterDraft(key: string): Promise<TaskFilterShared | null> {
  try {
    const res = await apiRequest(`/task-module/filter-drafts/${key}`, DraftRead);
    return fromDraftBody(res.filter);
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 404) return null;
    throw err;
  }
}
